import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import camelcaseKeys from 'camelcase-keys';
import { Mutex } from 'async-mutex';
import { ActivityJoinRecord } from './types';
import { getAccessToken, refreshToken } from '../../auth';
import { isOpaqueValueKey } from '../../utils/opaqueKeys';
import { CustomParams, InspectionItemType, MarkPaidParams, StripeResponse } from '../../modules/clientModule/types';

const mutex = new Mutex();
const snakeToCamel = data => {
  if (Array.isArray(data)) {
    return data.map(item => snakeToCamel(item));
  } if (data !== null && typeof data === 'object') {
    // One level at a time instead of `{ deep: true }`, so values behind an
    // opaque key keep their own keys verbatim — see utils/opaqueKeys.ts. A
    // deep convert renamed the inspection form's fields, and the resulting
    // names did not even match what the axios layer produced for the same
    // markup, so saved answers stopped lining up with the template.
    const shallow = camelcaseKeys(data);
    return Object.keys(shallow).reduce((acc, key) => {
      acc[key] = isOpaqueValueKey(key)
        ? shallow[key]
        : snakeToCamel(shallow[key]);
      return acc;
    }, {});
  }
  return data;
};

const baseQuery = fetchBaseQuery({
  baseUrl: '/api/v1/',
  prepareHeaders: headers => {
    headers.set('authorization', `Bearer ${getAccessToken()}`);
  },
});

const baseQueryWithCaseConversion = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshResult = await refreshToken(localStorage.getItem('refreshToken'));
        // @ts-ignore
        if (refreshResult) {
          // @ts-ignore
          localStorage.setItem('accessToken', refreshResult);
          // Retry the original query with new access token
          result = await baseQuery(args, api, extraOptions);
        } else {
          // Handle refresh failure (e.g., logout user)
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }
  if (result.data) {
    result.data = snakeToCamel(result.data);
  }
  return result;
};
// Define a service using a base URL and expected endpoints
export const apiSlice = createApi({
  reducerPath: 'allActivitiesClientSide',
  baseQuery: baseQueryWithCaseConversion,
  tagTypes: [
    'bulkInspections',
    'projectDocuments',
    'Inspection',
    'wikiDocument',
    'wikiNotes',
    'wikiGlossary',
    'wikiKeys',
    'wikiJurisdiction',
    'wikiPermitting',
    'thresholdForms',
    'thresholdFormFile',
    'Projects',
    'formFieldMappings',
    'ProjectImports',
  ],
  endpoints: builder => ({
    getAllActivitiesFlat: builder.query<ActivityJoinRecord[], number>({
      query: franchise_id => `project_activities/review_comments/1/getflat/?franchise_id=${franchise_id}`,
    }),
    getPendingInspections: builder.query<{ records: InspectionItemType[]; count: number }, any>({
      query: params => ({
        url: 'inspections/',
        params,
      }),
      providesTags: ['Inspection'],
      keepUnusedDataFor: 3600000,
      transformResponse: (response: any) => {
        const records = response.results.sort((a, b) => {
          if (new Date(a.inspectionDate) > new Date(b.inspectionDate)) return 1;
          if (new Date(a.inspectionDate) < new Date(b.inspectionDate)) return -1;
          return 0;
        });
        return {
          count: response.count,
          records: records.map(item => {
            const contact = `${item?.contactFirstName} ${item?.contactLastName}`.replace(/null/g, '');
            return {
              ...item,
              projectNamePermitName: `${item?.projectName} | ${item?.permitName}`,
              permitNumber: item?.permitNumber,
              projectAddress: item?.projectAddress,
              projectManager: `${item?.managerFirstName} ${item?.managerLastName}`,
              jurisdiction: item?.projectJurisdiction,
              inspectionType: item?.activityTypeName,
              inspectionTypeId: item?.inspectionType,
              inspectorName: `${item?.inspectorFirstName} ${item?.inspectorLastName}`,
              inspector: item?.inspector,
              id: item?.id,
              inspectionDate: item.inspectionDate ? item.inspectionDate : '',
              contact,
              contactPhone: item?.phoneNumber,
              cellPhone: item?.cellPhone,
            };
          }),
        };
      },
    }),
    updateInspectionFormMarkup: builder.mutation({
      query: arg => {
        return {
          url: `inspections/${arg.id}/update-form-markup/`,
          method: 'PUT',
          body: arg,
          headers: {
            'Content-Type': 'application/json; charset=UTF-8',
          },
        };
      },
      // Inspection Notes autosaves the markup, but the editor is opened from a
      // ROW of a cached inspection list (`getPendingInspections` keeps its data
      // for ~1000 hours). Without this the cache still held the pre-autosave
      // markup, so closing and reopening showed the old values — and worse,
      // InspectionTabs then persisted that stale copy back over the good one,
      // turning a display glitch into real data loss.
      //
      // Patched in place rather than `invalidatesTags: ['Inspection']`: the save
      // is debounced at 1.2s, so invalidating would refetch the whole inspection
      // list (form markup and all) every second or so while an inspector types.
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
        } catch {
          // The save failed; useFormMarkupTools already surfaces that. Leaving
          // the cache untouched keeps it consistent with the server.
          return;
        }

        apiSlice.util.selectInvalidatedBy(getState() as any, ['Inspection']).forEach(({ endpointName, originalArgs }) => {
          dispatch(
            apiSlice.util.updateQueryData(endpointName as any, originalArgs, (draft: any) => {
              // Endpoints tagged 'Inspection' return different shapes — a
              // { records } envelope here, plain arrays elsewhere.
              const list = Array.isArray(draft) ? draft : draft && draft.records;
              if (!Array.isArray(list)) return;
              const record = list.find((item: any) => item && item.id === arg.id);
              if (record) record.formMarkup = arg.form_markup;
            }),
          );
        });
      },
    }),
    updateInspection: builder.mutation({
      query: arg => {
        return {
          url: `inspections/${arg.id}/`,
          method: 'PUT',
          body: arg,
          headers: {
            'Content-Type': 'application/json; charset=UTF-8',
          },
        };
      },
      invalidatesTags: ['bulkInspections'],
    }),
    getAllInspectors: builder.query<{ label: string; value: string; trade: number[] }[], any>({
      query: () => 'account/get-all-users/?user_role=2',
      keepUnusedDataFor: 3600000, //
      transformResponse: (response: any) => {
        return response.map(item => ({
          label: `${item.firstName} ${item.lastName}`,
          value: `${item.id}`,
          email: item.email,
          // EA-1124: keep trades so the Pending Inspections dropdown can
          // filter inspectors by trade qualification.
          trade: (item.trade || []).map((t: any) => Number(typeof t === 'object' ? t.id : t)),
        }));
      },
    }),
    searchClients: builder.query<{ label: string; value: string }[], any>({
      query: params => `account/get-all-users/?offset=0&is_active=true&is_deleted=false&limit=100&user_role=3&search=${params.token}`,
      keepUnusedDataFor: 0, //
      transformResponse: (response: any) => {
        return response.map(item => ({
          label: [item.firstName, item.lastName].filter(Boolean).join(' '),
          value: `${item.id}`,
          email: item.email,
          companyName: item.companyName,
          clientType: item.clientType,
          userRole: item.userRole,
        }));
      },
    }),
    updateInspectionInspector: builder.mutation({
      query: arg => {
        return {
          url: `inspections/${arg.id}/`,
          method: 'PUT',
          body: arg,
          headers: {
            'Content-Type': 'application/json; charset=UTF-8',
          },
        };
      },
      invalidatesTags: ['Inspection'],
    }),
    sendCorrectionEmail: builder.mutation({
      query: arg => {
        return {
          url: `project_documents/send-correction-email/`,
          method: 'POST',
          body: arg,
        };
      },
    }),
    deleteProjectDocuments: builder.mutation({
      query: arg => {
        return {
          url: `project_documents/delete_documents/`,
          method: 'POST',
          body: arg,
        };
      },
      invalidatesTags: ['projectDocuments'],
    }),
    getAgingDetailsReport: builder.query({
      query: params => `reports/ar-aging-detail?franchise_id=${params}`,
      keepUnusedDataFor: 3600000,
    }),
    getVocabulary: builder.query({
      query: params =>
        `vocabularies/vocabulary-data/?vocabulary_entity=${params}&offset=0&is_active=true&is_deleted=false&limit=100000000&ordering=title`,
      keepUnusedDataFor: 3600000,
    }),
    getVocabularyCounty: builder.query({
      query: params => `vocabularies/county-appraiser-links/?offset=0&limit=100000000`,
      keepUnusedDataFor: 3600000,
    }),
    getProjects: builder.query({
      query: params => ({
        url: 'projects/',
        params,
      }),
      providesTags: ['Projects'],
      keepUnusedDataFor: 360000000,
    }),
    // Monday leads (EPIC project-import drafts) waiting for the office to review.
    getProjectImports: builder.query({
      query: () => ({
        url: 'epic/project-imports/',
        params: { status: 'draft', limit: 100, offset: 0 },
      }),
      providesTags: ['ProjectImports'],
    }),
    submitProjectImport: builder.mutation({
      query: ({ id, project }) => ({
        url: `epic/project-imports/${id}/submit/`,
        method: 'POST',
        body: { project },
      }),
      invalidatesTags: ['ProjectImports'],
    }),
    getProject: builder.query({
      query: params => ({
        url: `projects/${params.id}/`,
      }),
      keepUnusedDataFor: 360000000,
    }),
    getClientProjects: builder.query({
      query: params => ({
        url: 'client_projects/',
        params,
      }),
      keepUnusedDataFor: 360000000,
    }),
    getContactTypes: builder.query({
      query: params => ({
        url: 'contact-types/?offset=0&is_active=true&is_deleted=false&limit=100000000',
        params,
      }),
      keepUnusedDataFor: 360000000,
    }),
    getInvoiceDetails: builder.query({
      query: invoiceNumber => ({
        url: `app_invoice/get_invoice_details_by_number/?invoice_number=${invoiceNumber}`,
      }),
    }),
    markPaid: builder.mutation<StripeResponse, MarkPaidParams>({
      query: params => ({
        url: `app_invoice/mark_paid/`,
        method: 'POST',
        params,
      }),
    }),
    getProjectDocuments: builder.query<any, CustomParams>({
      query: params => ({
        url: '/project_activities/custom/',
        params,
      }),
      providesTags: ['projectDocuments'],
    }),
    getPermitConditions: builder.query<any, CustomParams>({
      query: params => ({
        url: '/project_activities/custom/',
        params,
      }),
      keepUnusedDataFor: 600,
    }),
    getBulkPermits: builder.query<any, CustomParams>({
      query: params => ({
        url: '/project_activities/custom/',
        params,
      }),
      providesTags: ['bulkInspections'],
    }),
    updateDocumentStatus: builder.mutation<any, any>({
      query: params => ({
        url: `project_documents/update_status/`,
        method: 'POST',
        body: params,
      }),
    }),
    deleteDocumentFile: builder.mutation<any, any>({
      query: params => ({
        url: `project_documents/delete_files/`,
        method: 'POST',
        body: params,
      }),
    }),
    resubmitDocumentFile: builder.mutation<any, any>({
      query: params => ({
        url: `project_activities/document_review/update_status/`,
        method: 'PATCH',
        body: params,
      }),
    }),
    getInspectionDocumentFileResubmitFlag: builder.query<any, any>({
      query: params => ({
        url: `project_activities/document_review/get_status/`,
        params,
        keepUnusedDataFor: 0,
      }),
    }),
    updateInspections: builder.mutation<any, any>({
      query: params => ({
        url: `inspections/update-inspections/${params.id}`,
        method: 'PATCH',
        body: params,
      }),
      invalidatesTags: ['Inspection'],
    }),
    setBulkInspections: builder.mutation<any, any>({
      query: params => ({
        url: `inspections/bulk_inspection_update/`,
        method: 'POST',
        body: params,
      }),
      invalidatesTags: ['bulkInspections'],
    }),
    getScheduledInspections: builder.query<any, any>({
      query: params => ({
        url: `inspections/scheduled_inspections_count/`,
        params,
        keepUnusedDataFor: 0,
      }),
      providesTags: ['bulkInspections'],
    }),
    // Follow-up date for an inspection date: 2 business days out, skipping weekends
    // and the permit's franchise holidays (Settings -> Scheduling). Same rule the
    // backend applies on save, so the form preview matches what gets stored.
    getInspectionFollowUpDate: builder.query<
      { followUp: string },
      { inspection_date: string; permit?: number }
    >({
      query: params => ({
        url: `inspections/follow-up-date/`,
        params,
      }),
    }),
    getFeesList: builder.query<any, any>({
      query: params => ({
        url: `/fees/`,
        params,
      }),
    }),
    getAllInspectionByClient: builder.query<any, any>({
      query: () => ({
        url: `client_projects/get-all-inspections`,
      }),
      providesTags: ['Inspection'],
      keepUnusedDataFor: 3600,
    }),
    getAllPermitInspections: builder.query<any, any>({
      query: id => ({
        url: `client_projects/get-all-permit-inspections?permit_id=${id}`,
      }),
      providesTags: ['Inspection'],
      keepUnusedDataFor: 3600,
    }),
    updateFranchiseData: builder.mutation<any, any>({
      query: params => ({
        url: `/franchises/${params.id}/`,
        method: 'PATCH',
        body: params.frm,
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetAllActivitiesFlatQuery,
  useLazyGetInspectionFollowUpDateQuery,
  useGetPendingInspectionsQuery,
  useGetAllInspectorsQuery,
  useSearchClientsQuery,
  useUpdateInspectionInspectorMutation,
  useGetAgingDetailsReportQuery,
  useGetVocabularyQuery,
  useGetVocabularyCountyQuery,
  useGetProjectsQuery,
  useGetProjectImportsQuery,
  useSubmitProjectImportMutation,
  useGetProjectQuery,
  useGetClientProjectsQuery,
  useGetContactTypesQuery,
  useGetInvoiceDetailsQuery,
  useMarkPaidMutation,
  useGetProjectDocumentsQuery,
  useUpdateDocumentStatusMutation,
  useDeleteDocumentFileMutation,
  useResubmitDocumentFileMutation,
  useSendCorrectionEmailMutation,
  useDeleteProjectDocumentsMutation,
  useUpdateInspectionsMutation,
  useGetPermitConditionsQuery,
  useGetInspectionDocumentFileResubmitFlagQuery,
  useGetAllInspectionByClientQuery,
  useGetFeesListQuery,
  useGetAllPermitInspectionsQuery,
  useUpdateFranchiseDataMutation,
  useUpdateInspectionFormMarkupMutation,
  useGetBulkPermitsQuery,
  useGetScheduledInspectionsQuery,
  useSetBulkInspectionsMutation,
  useUpdateInspectionMutation,
} = apiSlice;
